import { compact, every, map, sum, uniq, uniqBy } from 'es-toolkit/compat';
import pLimit from 'p-limit';

import type { FileToDownload } from 'components/atoms/DownloadFilesModal';
import { downloadBlob } from 'utils/exports';
import { createIndexedDbBlobStore, IndexedDbBlobStore } from './indexedDbBlobStore';
import { waitForNextPaint } from './waitForNextPaint';

export const ZIP_SOURCE_CONCURRENCY = 5;
export const ZIP_WARNING_FILE_COUNT = 100;
export const ZIP_WARNING_TOTAL_BYTES = 400 * 1024 * 1024;
const ZIP_STORAGE_OVERHEAD_BYTES = 16 * 1024 * 1024;

export interface FileToZip extends FileToDownload {
  contentLength?: number;
  caseId?: string;
  slideId?: string;
  orchestrationId?: string;
}

export type ZipDownloadPhase = 'fetching' | 'archiving' | 'saving';

export interface ZipDownloadProgress {
  phase: ZipDownloadPhase;
  completedFiles: number;
  totalFiles: number;
  loadedBytes: number;
  totalBytes?: number;
  archiveProgress?: number;
}

export interface ZipSelectionAssessment {
  knownTotalBytes?: number;
  warning?: string;
  blockingError?: string;
}

export const assessZipSelection = async (
  files: FileToZip[],
  estimateStorage: (() => Promise<StorageEstimate>) | undefined = navigator?.storage?.estimate?.bind(navigator.storage)
): Promise<ZipSelectionAssessment> => {
  const contentLengths = map(files, 'contentLength');
  const knownTotalBytes = every(contentLengths, (length) => length !== undefined)
    ? sum(contentLengths as number[])
    : undefined;
  const warningReasons = compact([
    files.length > ZIP_WARNING_FILE_COUNT ? `${files.length} files` : undefined,
    knownTotalBytes !== undefined && knownTotalBytes > ZIP_WARNING_TOTAL_BYTES
      ? `${Math.ceil(knownTotalBytes / (1024 * 1024))} MiB`
      : undefined,
  ]);
  const warning = warningReasons.length
    ? `This selection contains ${warningReasons.join(' and ')}. Consider exporting smaller case or slide batches.`
    : undefined;

  if (knownTotalBytes === undefined || !estimateStorage) {
    return { knownTotalBytes, warning };
  }

  let estimate: StorageEstimate;
  try {
    estimate = await estimateStorage();
  } catch {
    return { knownTotalBytes, warning };
  }
  const { quota, usage } = estimate;
  const availableBytes = quota !== undefined && usage !== undefined ? quota - usage : undefined;
  const requiredBytes = knownTotalBytes + ZIP_STORAGE_OVERHEAD_BYTES;
  return availableBytes !== undefined && availableBytes < requiredBytes
    ? {
        knownTotalBytes,
        warning,
        blockingError: `This export needs about ${Math.ceil(
          requiredBytes / (1024 * 1024)
        )} MiB of local storage. Reduce the selected cases or slides and try again.`,
      }
    : { knownTotalBytes, warning };
};

export interface ZipDownloadDependencies {
  fetchFile: typeof fetch;
  createStore: (operationId: string) => Promise<IndexedDbBlobStore>;
  saveBlob: (blob: Blob, archiveName: string) => void | Promise<void>;
  createLimit: (concurrency: number) => pLimit.Limit;
  createOperationId: () => string;
}

export class ZipDownloadError extends Error {
  failedFileNames: string[];

  // Reasons reported by files whose preparation failed, in the order of the failed files.
  failureReasons: string[];

  constructor(message: string, failedFileNames: string[] = [], failureReasons: string[] = []) {
    super(message);
    this.name = 'ZipDownloadError';
    this.failedFileNames = failedFileNames;
    this.failureReasons = failureReasons;
  }
}

const blobToStream = (blob: Blob): ReadableStream<Uint8Array> =>
  typeof blob.stream === 'function'
    ? (blob.stream() as ReadableStream<Uint8Array>)
    : new ReadableStream<Uint8Array>({
        start: (controller) =>
          new Promise<void>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              controller.enqueue(new Uint8Array(reader.result as ArrayBuffer));
              controller.close();
              resolve();
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsArrayBuffer(blob);
          }),
      });

const defaultDependencies: ZipDownloadDependencies = {
  fetchFile: (...args) => fetch(...args),
  createStore: createIndexedDbBlobStore,
  saveBlob: downloadBlob,
  createLimit: pLimit,
  createOperationId: () => crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
};

const sanitizeFileName = (fileName: string): string => {
  const sanitized = Array.from(fileName, (character) => {
    const characterCode = character.charCodeAt(0);
    return characterCode <= 31 || characterCode === 127 ? '_' : character;
  })
    .join('')
    .replace(/[\\/]/g, '_')
    .replace(/\.\.+/g, '_')
    .trim();
  return sanitized || 'download';
};

const getUrlFileName = (url: string): string | undefined => {
  try {
    const parsedUrl = new URL(url);
    const contentDisposition = parsedUrl.searchParams.get('response-content-disposition');
    const dispositionFileName = contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1];
    return dispositionFileName || parsedUrl.pathname.split('/').pop() || undefined;
  } catch {
    return undefined;
  }
};

export const getZipEntryFileNames = (files: FileToZip[]): string[] => {
  const usedNames = new Map<string, number>();

  return map(files, (file, index) => {
    const domainName =
      file.caseId && file.slideId && file.orchestrationId
        ? `case_${file.caseId}__slide_${file.slideId}__orch_${file.orchestrationId}.parquet`
        : undefined;
    const baseName = sanitizeFileName(
      domainName || file.fileName || getUrlFileName(file.url) || `download-${index + 1}`
    );
    const duplicateIndex = usedNames.get(baseName) || 0;
    usedNames.set(baseName, duplicateIndex + 1);
    if (!duplicateIndex) {
      return baseName;
    }

    const extensionIndex = baseName.lastIndexOf('.');
    return extensionIndex > 0
      ? `${baseName.slice(0, extensionIndex)}-${duplicateIndex + 1}${baseName.slice(extensionIndex)}`
      : `${baseName}-${duplicateIndex + 1}`;
  });
};

const getDisplayName = (file: FileToZip, index: number) =>
  file.title ||
  file.fileName ||
  (file.caseId && file.slideId ? `case ${file.caseId}, slide ${file.slideId}` : `file ${index + 1}`);

export const downloadFilesAsZip = async ({
  files,
  archiveName,
  signal,
  onProgress,
  dependencies = defaultDependencies,
}: {
  files: FileToZip[];
  archiveName: string;
  signal: AbortSignal;
  onProgress?: (progress: ZipDownloadProgress) => void;
  dependencies?: ZipDownloadDependencies;
}): Promise<void> => {
  if (signal.aborted) {
    throw signal.reason || new DOMException('ZIP download was cancelled', 'AbortError');
  }

  // An id marks a distinct selected entry, so two entries may share a URL; without an id the URL is the identity.
  const uniqueFiles = uniqBy(files, (file) => `${file.id ?? ''}\u0000${file.url}`);
  const entryNames = getZipEntryFileNames(uniqueFiles);
  const contentLengthsByFile = new Map<number, number>();
  uniqueFiles.forEach((file, index) => {
    if (file.contentLength !== undefined) contentLengthsByFile.set(index, file.contentLength);
  });
  const getTotalBytes = () =>
    contentLengthsByFile.size === uniqueFiles.length ? sum([...contentLengthsByFile.values()]) : undefined;
  const loadedBytesByFile = new Map<number, number>();
  let completedFiles = 0;
  let store: IndexedDbBlobStore | undefined;
  const internalController = new AbortController();
  const abortInternal = () => internalController.abort(signal.reason);
  signal.addEventListener('abort', abortInternal, { once: true });

  const reportFetchingProgress = () => {
    if (!internalController.signal.aborted) {
      onProgress?.({
        phase: 'fetching',
        completedFiles,
        totalFiles: uniqueFiles.length,
        loadedBytes: sum([...loadedBytesByFile.values()]),
        totalBytes: getTotalBytes(),
      });
    }
  };

  try {
    store = await dependencies.createStore(dependencies.createOperationId());
    const { ZipWriter } = await import('@zip.js/zip.js');
    const zipWriter = new ZipWriter(store.writable, { level: 0, dataDescriptor: true, keepOrder: false });
    const limit = dependencies.createLimit(ZIP_SOURCE_CONCURRENCY);
    reportFetchingProgress();

    const tasks = map(uniqueFiles, (file, index) =>
      limit(async () => {
        try {
          if (internalController.signal.aborted) {
            throw internalController.signal.reason || new DOMException('ZIP download was cancelled', 'AbortError');
          }

          let entryBody: ReadableStream<Uint8Array>;
          if (file.prepare) {
            const prepared = await file.prepare(internalController.signal);
            entryBody = blobToStream(prepared);
          } else {
            const response = await dependencies.fetchFile(file.url, { signal: internalController.signal });
            if (!response.ok || !response.body) {
              throw new ZipDownloadError(`Could not retrieve ${getDisplayName(file, index)}`, [
                getDisplayName(file, index),
              ]);
            }
            const contentLengthHeader = response.headers?.get('Content-Length');
            const responseContentLength = contentLengthHeader === null ? Number.NaN : Number(contentLengthHeader);
            if (
              !contentLengthsByFile.has(index) &&
              Number.isFinite(responseContentLength) &&
              responseContentLength >= 0
            ) {
              contentLengthsByFile.set(index, responseContentLength);
              reportFetchingProgress();
            }
            entryBody = response.body;
          }

          await zipWriter.add(entryNames[index], entryBody, {
            level: 0,
            dataDescriptor: true,
            signal: internalController.signal,
            onprogress: (loadedBytes) => {
              loadedBytesByFile.set(index, loadedBytes);
              reportFetchingProgress();
            },
          });
          completedFiles += 1;
          reportFetchingProgress();
        } catch (error) {
          internalController.abort(error);
          throw error;
        }
      })
    );

    const taskResults = await Promise.allSettled(tasks);
    const failures = uniq(
      compact(
        map(taskResults, (result, index) => {
          if (result.status === 'fulfilled') {
            return undefined;
          }
          return result.reason instanceof ZipDownloadError
            ? result.reason.failedFileNames[0]
            : getDisplayName(uniqueFiles[index], index);
        })
      )
    );
    if (failures.length) {
      internalController.abort();
      if (signal.aborted) {
        throw signal.reason || new DOMException('ZIP download was cancelled', 'AbortError');
      }
      const failureReasons = uniq(
        compact(
          map(taskResults, (result, index) =>
            result.status === 'rejected' &&
            uniqueFiles[index].prepare &&
            result.reason instanceof Error &&
            result.reason.name !== 'AbortError'
              ? result.reason.message
              : undefined
          )
        )
      );
      throw new ZipDownloadError(
        `Could not create the complete ZIP archive (${failures.length} files failed)`,
        failures,
        failureReasons
      );
    }

    onProgress?.({
      phase: 'archiving',
      completedFiles,
      totalFiles: uniqueFiles.length,
      loadedBytes: sum([...loadedBytesByFile.values()]),
      totalBytes: getTotalBytes(),
      archiveProgress: 0,
    });
    // ZIP finalization can complete before React paints, hiding the archive phase behind the subsequent saving state.
    await waitForNextPaint();
    if (internalController.signal.aborted) {
      throw internalController.signal.reason || new DOMException('ZIP download was cancelled', 'AbortError');
    }
    await zipWriter.close(undefined, {
      onprogress: (progress, total) =>
        onProgress?.({
          phase: 'archiving',
          completedFiles,
          totalFiles: uniqueFiles.length,
          loadedBytes: sum([...loadedBytesByFile.values()]),
          totalBytes: getTotalBytes(),
          archiveProgress: total ? progress / total : undefined,
        }),
    });
    if (internalController.signal.aborted) {
      throw internalController.signal.reason || new DOMException('ZIP download was cancelled', 'AbortError');
    }

    onProgress?.({
      phase: 'saving',
      completedFiles,
      totalFiles: uniqueFiles.length,
      loadedBytes: sum([...loadedBytesByFile.values()]),
      totalBytes: getTotalBytes(),
    });
    await dependencies.saveBlob(await store.getBlob('application/zip'), sanitizeFileName(archiveName));
  } catch (error) {
    internalController.abort();
    throw error;
  } finally {
    signal.removeEventListener('abort', abortInternal);
    await store?.cleanup();
  }
};
